import { Link } from '../../../components/Link';
import { PublicFooter } from '../../../components/PublicFooter';
import { PublicHeader } from '../../../components/PublicHeader';
import { SITE_ORIGIN, Seo, breadcrumbFor, toTrailingSlash } from '../../../lib/seo';
import { LEARN_ARTICLES, publishedClusters, pillarOf, spokesOf } from './learnArticles';
import { TERMSET_ID, TERMSET_NAME } from './LearnPageLayout';

/**
 * `/learn/` — the index of the informational content layer, and the page that declares the
 * DefinedTermSet every article links itself into.
 *
 * Grouped by CLUSTER with the pillar surfaced first, because that is the actual information
 * architecture — a flat alphabetical list would hide which page is the entry point for a topic.
 *
 * Each term is emitted as a TYPED node (@id + @type + name + url) rather than a bare `{"@id": …}`
 * reference: a lone @id is a reference the page would then have to declare, which the site-wide
 * "no dangling @id references" contract rightly rejects.
 */
export default function LearnIndexPage() {
  const clusters = publishedClusters();

  return (
    <>
      <Seo
        title="Email authentication, explained"
        description="Plain explanations of SPF, DKIM and DMARC — what each record does, where it lives, and why yours is failing. Written from the checks our own tools run."
        path="/learn"
        keywords="email authentication, DKIM, SPF, DMARC, email deliverability"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'DefinedTermSet',
            '@id': TERMSET_ID,
            name: TERMSET_NAME,
            url: `${SITE_ORIGIN}/learn/`,
            hasDefinedTerm: LEARN_ARTICLES.map((a) => ({
              '@id': `${SITE_ORIGIN}${toTrailingSlash(`/learn/${a.slug}`)}#term`,
              '@type': 'DefinedTerm',
              name: a.term,
              url: `${SITE_ORIGIN}${toTrailingSlash(`/learn/${a.slug}`)}`,
            })),
          },
          breadcrumbFor([
            { name: 'Home', path: '/' },
            { name: 'Learn', path: '/learn' },
          ]),
        ]}
      />
      <div className="mx-auto max-w-[1180px] overflow-x-clip px-6 pb-20">
        <PublicHeader />

        <section className="mx-auto mt-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accentdeep/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Reference · No signup
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
            Email authentication, explained
          </h1>
          <p className="mt-4 text-[15.5px] leading-relaxed text-muted">
            SPF, DKIM and DMARC are three small DNS records that decide whether your mail is
            trusted, quietly filtered, or refused outright. The specs that define them are precise
            and almost unreadable; the vendor guides that explain them are usually a setup wizard
            with the reasoning removed. These pages sit in between — what each record is, where it
            actually lives, what breaks it, and how to tell which of those is happening to you.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-muted">
            Everything here is written from the checks our own free tools run, so the specifics —
            which selectors we probe, which limits we enforce, what counts as a failure — match
            what you get back when you run one.
          </p>
        </section>

        {clusters.map((c) => {
          const pillar = pillarOf(c.key);
          const spokes = spokesOf(c.key);
          return (
            <section key={c.key} className="mx-auto mt-14 max-w-3xl">
              <h2 className="font-display text-2xl font-bold tracking-tight">{c.name}</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-muted">{c.summary}</p>

              {/* The pillar gets visual weight because it is the entry point, not because it is
                  longer. A flat list of eight cards tells a reader nothing about where to start. */}
              {/* See the data-linkrail note in LearnPageLayout — card text is navigation, and the
                  duplication guard excludes it before comparing pages. */}
              <nav data-linkrail aria-label={`${c.name} articles`}>
              <Link
                to={`/learn/${pillar.slug}`}
                className="mt-6 block rounded-2xl border border-accentdeep/40 bg-accentdeep/10 p-6 transition hover:border-accentdeep hover:bg-accentdeep/15"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent">
                  Start here
                </div>
                <div className="mt-2 font-display text-[19px] font-bold tracking-tight">
                  {pillar.title} →
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{pillar.blurb}</p>
              </Link>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {spokes.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/learn/${a.slug}`}
                    className="rounded-2xl border border-bordersoft bg-panel p-5 transition hover:border-accentdeep hover:bg-panel2"
                  >
                    <div className="font-display text-[15.5px] font-bold tracking-tight">
                      {a.title} →
                    </div>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-muted">{a.blurb}</p>
                  </Link>
                ))}
              </div>
              </nav>
            </section>
          );
        })}

        <PublicFooter />
      </div>
    </>
  );
}
